/**
 * LocationFieldType — F12.B T12.B.4.
 * DDL: 2 kolumny per pole: <slug>__lat NUMERIC(10,7), <slug>__lng NUMERIC(10,7).
 * Frontend przekazuje { lat, lng }, API pakuje/odpakuje do/z kolumn DDL.
 */
import { MapPin } from 'lucide-react';

import { FieldRegistry } from '../FieldRegistry';

interface LocValue {
  lat: number;
  lng: number;
}

const INPUT_CLASS =
  'w-full rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-500 focus:ring-1 focus:ring-accent-500 focus:outline-none';

FieldRegistry.register({
  type: 'location',
  renderInput: ({ value, onChange }) => {
    const loc: LocValue = (value as LocValue) || { lat: 52.237049, lng: 21.017532 };
    const set = (key: 'lat' | 'lng', v: string) => {
      const n = parseFloat(v);
      if (!isNaN(n)) onChange({ ...loc, [key]: n });
    };

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              Szerokość (Latitude)
            </label>
            <input
              type="number"
              step="0.000001"
              value={loc.lat || ''}
              onChange={(e) => set('lat', e.target.value)}
              className={INPUT_CLASS}
              placeholder="52.237049"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">
              Długość (Longitude)
            </label>
            <input
              type="number"
              step="0.000001"
              value={loc.lng || ''}
              onChange={(e) => set('lng', e.target.value)}
              className={INPUT_CLASS}
              placeholder="21.017532"
            />
          </div>
        </div>
        <p className="rounded bg-bg-secondary p-2 text-xs text-text-muted">
          Sprawdź współrzędne na{' '}
          <a
            href={`https://www.google.com/maps/search/${loc.lat},${loc.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-600 hover:underline"
          >
            Google Maps
          </a>
        </p>
      </div>
    );
  },
  renderCell: (_field, value) => {
    if (!value || typeof value !== 'object') return '—';
    const { lat, lng } = value as LocValue;
    if (!lat || !lng) return '—';
    return (
      <div className="flex items-center gap-1.5">
        <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-danger-500" />
        <a
          href={`https://www.google.com/maps/search/${lat},${lng}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-accent-600 hover:underline"
        >
          {lat.toFixed(4)}, {lng.toFixed(4)}
        </a>
      </div>
    );
  },
  validate: (value, field) => {
    if (!value) return field.isRequired ? 'Pole wymagane' : null;
    if (typeof value !== 'object') return null;
    const { lat, lng } = value as LocValue;
    if (lat < -90 || lat > 90) return 'Szerokość musi być między -90 a 90';
    if (lng < -180 || lng > 180) return 'Długość musi być między -180 a 180';
    return null;
  },
  defaultValue: { lat: 52.237049, lng: 21.017532 },
});
